/**
 * @module components/ui/SectionHeader
 * @description
 *   Section title row: IconTile + title + subtitle.
 *   Optional collapse control (top-right) for major analysis outer cards —
 *   keeps the header visible and lets parents hide the body to shorten
 *   scroll, especially on mobile.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import IconTile from './IconTile';

/**
 * @param {object} props
 * @param {React.ReactNode} [props.icon]
 * @param {string} [props.gradient='insight'] — IconTile gradient preset
 * @param {'sm'|'md'|'lg'|'xl'} [props.iconSize='lg']
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.subtitle]
 * @param {string} [props.className]
 * @param {string} [props.titleClassName]
 * @param {boolean} [props.stacked] — column on mobile (default true)
 * @param {boolean} [props.collapsed=false] — when true, parent hides section body
 * @param {() => void} [props.onToggleCollapse] — shows chevron control when set
 */
export default function SectionHeader({
  icon,
  gradient = 'insight',
  iconSize = 'lg',
  title,
  subtitle,
  className = '',
  titleClassName = 'font-display text-3xl sm:text-4xl font-bold text-gradient-blue tracking-tight',
  stacked = true,
  collapsed = false,
  onToggleCollapse,
}) {
  const collapsible = typeof onToggleCollapse === 'function';

  return (
    <div
      className={`relative ${
        stacked
          ? 'flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4'
          : 'flex items-center space-x-4'
      } ${collapsed ? '' : 'mb-8'} ${collapsible ? 'pr-12' : ''} ${className} ${
        collapsed ? '!mb-0' : ''
      }`.trim()}
    >
      {icon && (
        <IconTile size={iconSize} gradient={gradient}>
          {icon}
        </IconTile>
      )}
      <div className="min-w-0">
        <h2 className={titleClassName}>{title}</h2>
        {subtitle && (
          <p className="text-critic-text-secondary font-normal text-sm sm:text-base leading-relaxed max-w-xl">
            {subtitle}
          </p>
        )}
      </div>

      {collapsible ? (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="absolute top-0 right-0 z-10 inline-flex items-center justify-center rounded-xl border border-white/[0.1] bg-black/25 p-2 text-critic-text-secondary hover:text-critic-text-primary hover:border-white/[0.18] hover:bg-black/40 transition-colors"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand section' : 'Collapse section'}
        >
          <ChevronDown
            className={`h-5 w-5 transition-transform duration-300 ease-critic-out ${
              collapsed ? 'rotate-0' : 'rotate-180'
            }`}
            aria-hidden
          />
        </button>
      ) : null}
    </div>
  );
}
